import Dropdown from 'components/Dropdown/Dropdown';
import MenuGroup from './MenuGroup';
import MenuLink from './MenuLink';
import NewBadge from 'tailwind-components/NewBadge/NewBadge';
import PropTypes from 'prop-types';
import React, { useMemo, useState } from 'react';
import LmsInviteModal from 'tailwind-components/LmsInvite/LmsInviteModal';
import { ONEILL_MAIN_AGENCY_ID, USER_TYPES } from 'constants/enum';
import { isAgentRankLimited } from 'helpers/agents';
import { getCompany } from 'helpers/getCompanyVariables';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useLocation } from 'react-router-dom';
import { toggleSidebar } from 'state/actions/site';
import useMobile from 'hooks/useMobile';
import useViewAs from 'hooks/useViewAs';
import { cn } from 'lib/utils';
import { SidebarNavProvider } from './sidebarNavContext';
import {
  getActiveSidebarDropdownGroup,
  SIDEBAR_DROPDOWN_GROUP
} from './sidebarDropdownRouteGroup';
import {
  areAppointmentsDisabled,
  areCommissionsTabVisible,
  areOnboardingInvitesDisabled,
  isCommissionsOverviewTabVisible,
  isCallReviewsTabVisible,
  isLeadPartnersTabVisible
} from 'helpers/viewing-rules';
import { SUNSHINE_LICENSING_SCHOOL_URL } from 'constants/externalLinks';

const company = getCompany();
const isSunshine = company === 'sunshine';

// sunshine shifts the commissions tab names by one: the overview route is its
// book of business insights, and statements take over the overview name
const COMMISSIONS_OVERVIEW_LABEL = isSunshine
  ? 'Book of Business Insights'
  : 'Commissions Overview';
const STATEMENTS_LABEL = isSunshine ? 'Commissions Overview' : 'Statements';

// hoisted so the memoized MenuLink doesn't re-render on every parent render
const NEW_BADGE = <NewBadge />;

function pathIsAgencyDashboard(pathname) {
  return /^\/agency\/dashboard\/[^/]+$/.test(pathname ?? '');
}

function pathIsAgencySettings(pathname) {
  return /^\/agency\/settings\/[^/]+$/.test(pathname ?? '');
}

function pathIsAgencyAppointments(pathname) {
  return /^\/agency\/appointments\/[^/]+$/.test(pathname ?? '');
}

const sidebarWrapper = 'flex flex-col h-[calc(100vh-92px)] w-full';

const scrollableNavBase =
  'flex-1 min-h-0 overflow-y-auto overflow-x-hidden w-full [&_ul]:w-full [&_ul_li]:mb-2 [&_ul_li]:relative [&_ul_li]:w-full';

const innerContainerShrinked =
  '[&_ul_li:first-child]:px-0 [&_ul_li:first-child]:flex [&_ul_li:first-child]:justify-center [&_ul_li:first-child]:items-center [&_ul_li:first-child]:text-xs ' +
  '[&_.sidebar-link-text]:hidden [&_ul_li>a]:pl-0 [&_ul_li>a]:justify-center [&_ul_li>a]:flex [&_ul_li>a]:items-center [&_ul_li>a_svg]:mr-0 ' +
  '[&_ul_li_div>div>svg]:right-[20%] [&_ul_li_div>div>svg]:w-2.5 [&_ul_li_div>div>svg]:h-2.5 [&_ul_li_div>div>a]:pl-[22px]';

function SidebarContent({ activePage, isShrinked = false }) {
  const { type } = useParams();
  const { pathname } = useLocation();
  const user = useSelector(state => state.user.zohoData);
  const { targetId: viewAsAgent } = useViewAs();

  const showLeadPartnersTab = isLeadPartnersTabVisible(user);
  const showCallReviews = isCallReviewsTabVisible(user, viewAsAgent);
  const showBookOfBusinessTab = user.Book_of_Business_Tab === 'Show';
  const showCustomersTab = user.Customers_Tab === 'Show';
  const showBobCommissionsTab = user.NPN && !user.Hide_BOB_Commissions_Tab;
  const showCommissionsTabs = areCommissionsTabVisible(user);
  // skipCommissionsTabCheck is true because we want to show the commissions overview tab even if the commissions tab is hidden
  const showCommissionsOverviewTab = isCommissionsOverviewTabVisible(
    user,
    true
  );

  const { actionableAppointments } = useSelector(state => state.appointments);

  const showApptWarning = actionableAppointments.length > 0;

  const appointmentsDisabled = areAppointmentsDisabled(user);

  const isAgentLimited = isAgentRankLimited(user.Rank);

  const [isLmsModalOpen, setIsLmsModalOpen] = useState(false);
  const dispatch = useDispatch();
  const isMobile = useMobile();

  function openLmsModal(e) {
    e.preventDefault();
    // the mobile sidebar sits on top of the page, close it so the modal is usable
    if (isMobile) dispatch(toggleSidebar(false));
    setIsLmsModalOpen(true);
  }

  const activeDropdownGroup = useMemo(
    () =>
      getActiveSidebarDropdownGroup({
        pathname,
        activePage,
        type,
        company
      }),
    [pathname, activePage, type, company]
  );

  return (
    <div className={sidebarWrapper}>
      <SidebarNavProvider isShrinked={isShrinked} isDarkPanel={isSunshine}>
        <div
          className={cn(
            scrollableNavBase,
            isShrinked && innerContainerShrinked
            // isShrinked ? '' : 'show-scrollbar'
          )}
        >
          <MenuGroup title='Dashboards'>
            <MenuLink
              text='Home'
              icon='home'
              to='/home'
              active={activePage === 'Home'}
              data-tour='home'
            />
            {company === 'oneill' && !isAgentLimited && (
              <Dropdown
                forceExpanded={
                  activeDropdownGroup ===
                  SIDEBAR_DROPDOWN_GROUP.OFF_MARKET_TOOLS
                }
                pathname={pathname}
                mainComponent={
                  <MenuLink
                    key='offMarketToolsHeader'
                    text='Off-Market Tools'
                    icon='wrench'
                    active={activePage === 'Bookshelf'}
                    extraIconFill
                    extraIcon='grade'
                    onClick={e => {
                      e.preventDefault();
                    }}
                  />
                }
              >
                <MenuLink
                  key='bookshelf'
                  text='Bookshelf'
                  icon='libraryBig'
                  to='/off-market/bookshelf'
                  active={activePage === 'Bookshelf'}
                />
              </Dropdown>
            )}
          </MenuGroup>

          {user.Position_at_Agency === USER_TYPES.ADMIN && user.Account_Name && (
            <>
              {company === 'sunshine' ? (
                <MenuGroup title='Agency'>
                  <MenuLink
                    text='Agency Management'
                    icon='agency'
                    to='/agencies'
                    active={activePage === 'Agency Management'}
                  />
                </MenuGroup>
              ) : (
                <MenuGroup title='Agency'>
                  <Dropdown
                    forceExpanded={
                      activeDropdownGroup === SIDEBAR_DROPDOWN_GROUP.AGENCY
                    }
                    pathname={pathname}
                    mainComponent={
                      <MenuLink
                        key='agencyManagementHeader'
                        text='Agency Management'
                        icon='agency'
                        active={activePage === 'Agency Management'}
                        data-tour='agency-management'
                        onClick={e => {
                          e.preventDefault();
                        }}
                      />
                    }
                  >
                    <MenuLink
                      key='agencyHierarchy'
                      text='Hierarchy'
                      icon='agencyHierarchy'
                      to='/agency/hierarchy'
                      active={
                        type === 'Agency Hierarchy' ||
                        pathname === '/agency/hierarchy'
                      }
                    />
                    <MenuLink
                      key='agencyDashboard'
                      text='Dashboard'
                      icon='agencyDashboard'
                      to={`/agency/dashboard/${user.Account_Name.id}`}
                      active={pathIsAgencyDashboard(pathname)}
                      data-tour='agency-dashboard'
                    />
                    <MenuLink
                      key='agencySettings'
                      text={
                        areOnboardingInvitesDisabled(user.agencyData)
                          ? 'Settings'
                          : 'Settings & Invites'
                      }
                      icon='agencySettings'
                      to={`/agency/settings/${user.Account_Name.id}`}
                      active={pathIsAgencySettings(pathname)}
                      data-tour='agency-settings'
                    />
                    <MenuLink
                      key='agencyAppointments'
                      text='Appointments Center'
                      icon='agencyAppointments'
                      to={`/agency/appointments/${user.Account_Name.id}`}
                      active={
                        type === 'Agency Appointments' ||
                        pathIsAgencyAppointments(pathname)
                      }
                      data-tour='agency-appointments'
                    />
                  </Dropdown>
                </MenuGroup>
              )}
            </>
          )}

          <MenuGroup title='Agent'>
            <MenuLink
              text='My Profile'
              icon='agentInfo'
              to='/my-profile'
              active={activePage === 'My Profile'}
            />
            {!isAgentLimited && !appointmentsDisabled && (
              <MenuLink
                text='Carrier Appointments'
                icon='myAppointments'
                to='/carrier-appointments'
                active={activePage === 'My Appointments'}
                extraIcon={showApptWarning ? 'warning' : ''}
                extraIconFill={showApptWarning}
                tabType={showApptWarning ? 'danger' : 'default'}
                data-tour='carrier-appointments'
              />
            )}
            {!isAgentLimited && (showCustomersTab || showBobCommissionsTab) && (
              <Dropdown
                forceExpanded={
                  activeDropdownGroup === SIDEBAR_DROPDOWN_GROUP.CUSTOMERS
                }
                pathname={pathname}
                mainComponent={
                  <MenuLink
                    text='Customers'
                    icon='people'
                    data-tour='customers'
                    onClick={e => {
                      e.preventDefault();
                    }}
                    active={
                      activePage === 'Customers' ||
                      activePage === 'CustomersBob' ||
                      activePage === 'Risk Management' ||
                      activePage === 'CustomersCommissionsReco'
                    }
                  />
                }
              >
                {showCustomersTab && (
                  <>
                    <MenuLink
                      text='Submissions'
                      icon='policy'
                      to='/customers/policies'
                      active={activePage === 'Customers'}
                      data-tour='submissions'
                    />
                    {showBookOfBusinessTab && (
                      <>
                        <MenuLink
                          text='Book of Business'
                          icon='book'
                          to='/customers/book-of-business'
                          active={activePage === 'CustomersBob'}
                        />
                        {company === 'sunshine' && (
                          <MenuLink
                            text='Risk Management'
                            icon='riskManagement'
                            to='/customers/risk-management'
                            active={activePage === 'Risk Management'}
                          />
                        )}
                      </>
                    )}
                  </>
                )}

                {showBobCommissionsTab && (
                  <MenuLink
                    text='Commissions Reconciliation'
                    icon='bonus'
                    to='/customers/commission-reconciliation'
                    active={activePage === 'CustomersCommissionsReco'}
                  />
                )}
              </Dropdown>
            )}
            {(showCommissionsTabs || showCommissionsOverviewTab) && (
              <Dropdown
                forceExpanded={
                  activeDropdownGroup === SIDEBAR_DROPDOWN_GROUP.COMMISSIONS
                }
                pathname={pathname}
                mainComponent={
                  <MenuLink
                    key='commissionsHeader'
                    text='Commissions'
                    icon='commissions'
                    onClick={e => {
                      e.preventDefault();
                    }}
                    active={
                      activePage === 'Commissions' ||
                      activePage === 'CustomersCommissions'
                    }
                  />
                }
              >
                {showCommissionsOverviewTab && (
                  <MenuLink
                    key='commissionsOverviewBody'
                    text={COMMISSIONS_OVERVIEW_LABEL}
                    icon='bonus'
                    to='/commissions/overview'
                    active={activePage === 'CustomersCommissions'}
                  />
                )}

                {showCommissionsTabs && (
                  <>
                    <MenuLink
                      key='statementsBody'
                      text={STATEMENTS_LABEL}
                      icon='download'
                      to='/commissions/statements'
                      active={type === 'statements'}
                    />

                    {showBookOfBusinessTab &&
                    user?.agencyData?.id === '4738157000049341001' ? (
                      <MenuLink
                        key='bookOfBusinessBody'
                        text='Book of business'
                        icon='download'
                        to='/commissions/book-of-business'
                        active={type === 'book-of-business'}
                      />
                    ) : null}

                    {company === 'oneill' && (
                      <>
                        <MenuLink
                          key='commissionsBody'
                          text='Rates'
                          icon='commissionRates'
                          to='/commissions/rates'
                          active={type === 'rates'}
                        />

                        {(user.Bonus_sub_tab === 'Force Show' ||
                          (user.Position_at_Agency === USER_TYPES.ADMIN &&
                            user.agencyData?.Parent_Account?.id ===
                              ONEILL_MAIN_AGENCY_ID &&
                            user.Bonus_sub_tab !== 'Hide') ||
                          (user.Bonus_sub_tab !== 'Hide' &&
                            !user.agencyData
                              ?.Hide_Bonus_Rates_from_Agents)) && (
                          <MenuLink
                            key='bonusesBody'
                            text='Bonuses'
                            icon='bonus'
                            to='/commissions/bonuses'
                            active={type === 'bonuses'}
                          />
                        )}
                      </>
                    )}
                  </>
                )}
              </Dropdown>
            )}
          </MenuGroup>

          {!isAgentLimited && (
            <MenuGroup title='General'>
              {company === 'sunshine' && (
                <>
                  <MenuLink
                    key='reconciliationForms'
                    text='Reconciliation Forms'
                    icon='policy'
                    to='/reconciliation-forms'
                    active={activePage === 'Reconciliation Forms'}
                  />
                  {showCallReviews && (
                    <MenuLink
                      key='callReviews'
                      text='Call Reviews'
                      icon='files'
                      to='/call-reviews'
                      active={activePage === 'Call Reviews'}
                    />
                  )}
                  <Dropdown
                    mainComponent={
                      <MenuLink
                        key='externalLinksHeader'
                        text='External Links'
                        icon='openInNew'
                        onClick={e => {
                          e.preventDefault();
                        }}
                      />
                    }
                  >
                    <MenuLink
                      key='LMS'
                      text='LMS Link'
                      icon='openInNew'
                      onClick={openLmsModal}
                    />
                    <MenuLink
                      key='E123'
                      text='E123 Link'
                      icon='openInNew'
                      to='https://administration123.com/manage/index.cfm'
                      target='_blank'
                      rel='noreferrer'
                      external
                    />
                    <MenuLink
                      key='HealthSherpa'
                      text='HealthSherpa Link'
                      icon='openInNew'
                      to='https://www.healthsherpa.com/sessions/new'
                      target='_blank'
                      rel='noreferrer'
                      external
                    />
                    <MenuLink
                      key='licensingSchool'
                      text='Educational Portal - CE'
                      icon='openInNew'
                      to={SUNSHINE_LICENSING_SCHOOL_URL}
                      target='_blank'
                      rel='noreferrer'
                      external
                    />
                  </Dropdown>
                </>
              )}
              <MenuLink
                text='Resources'
                icon='resources'
                to='/resources'
                active={activePage === 'Resources'}
              />
              {company === 'oneill' && user.isOepWindowActive && (
                <MenuLink
                  key='oepUpdates'
                  text='OEP Updates'
                  icon='menuBook'
                  to='/oep-updates'
                  active={activePage === 'OEP Updates'}
                  badge={NEW_BADGE}
                  data-tour='oep-updates'
                />
              )}
              {company === 'oneill' && (
                <Dropdown
                  forceExpanded={
                    activeDropdownGroup === SIDEBAR_DROPDOWN_GROUP.MARKETING
                  }
                  pathname={pathname}
                  mainComponent={
                    <MenuLink
                      key='marketingServicesHeader'
                      text='Marketing Services'
                      icon='marketing'
                      active={activePage === 'Marketing Services'}
                      data-tour='marketing-services'
                      onClick={e => {
                        e.preventDefault();
                      }}
                    />
                  }
                >
                  <MenuLink
                    key='contentLibrary'
                    text='Content Library'
                    icon='library'
                    to='/marketing/content-library'
                    active={
                      type === 'Content Library' ||
                      pathname === '/marketing/content-library'
                    }
                    data-tour='content-library'
                  />
                  {showLeadPartnersTab && (
                    <MenuLink
                      key='leads'
                      text='Leads'
                      icon='lead'
                      to='/marketing/leads'
                      active={
                        type === 'Leads' || pathname === '/marketing/leads'
                      }
                    />
                  )}
                </Dropdown>
              )}
            </MenuGroup>
          )}
        </div>
      </SidebarNavProvider>
      {isLmsModalOpen && (
        <LmsInviteModal onClose={() => setIsLmsModalOpen(false)} />
      )}
    </div>
  );
}

SidebarContent.propTypes = {
  activePage: PropTypes.string,
  isShrinked: PropTypes.bool
};

export default React.memo(SidebarContent);
